import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  WbButton,
  WbHeaderButton,
  WbIcon,
} from "@workbench/react";
import { useGetCurrentUserQuery } from "../../store/authApi";
import "./UserProfileMenu.css";

function displayName(user: { name?: string; givenName?: string; familyName?: string; sub?: string }) {
  if (user.name) return user.name;
  const fullName = [user.givenName, user.familyName].filter(Boolean).join(" ");
  return fullName || user.sub || "User";
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function rightViewportPadding(): number {
  const rootFontSize = Number.parseFloat(
    getComputedStyle(document.documentElement).fontSize,
  );
  return rootFontSize * 2;
}

interface UserProfileMenuProps {
  onLogout?: () => void;
}

export default function UserProfileMenu({
  onLogout,
}: UserProfileMenuProps): React.ReactElement | null {
  const { data: user } = useGetCurrentUserQuery();
  const [menuOpen, setMenuOpen] = useState(false);
  const [panelPos, setPanelPos] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  });
  const buttonRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLWbHeaderButtonElement>(null);

  const closeMenu = useCallback((restoreFocus = false) => {
    setMenuOpen(false);
    if (restoreFocus) {
      triggerRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    function handleMouseDown(e: MouseEvent) {
      const path = e.composedPath();
      const insideButton = buttonRef.current && path.includes(buttonRef.current);
      const insidePanel = panelRef.current && path.includes(panelRef.current);
      if (!insideButton && !insidePanel) closeMenu();
    }
    document.addEventListener("mousedown", handleMouseDown, { passive: true });
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    if (!menuOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") closeMenu(true);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen, closeMenu]);

  if (!user) return null;

  const name = displayName(user);
  const email = user.email ?? "Email not available";

  function toggleMenu() {
    setMenuOpen((prev) => {
      if (!prev && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const right = Math.max(rightViewportPadding(), window.innerWidth - rect.right);
        setPanelPos({
          top: rect.bottom + window.scrollY,
          right,
        });
      }
      return !prev;
    });
  }

  function logout() {
    if (onLogout) {
      onLogout();
      return;
    }
    window.location.assign("/api/auth/logout");
  }

  const menu = menuOpen
    ? ReactDOM.createPortal(
        <div
          ref={panelRef}
          className="user-profile-menu__panel"
          role="menu"
          aria-label="User menu"
          data-testid="user-profile-menu-panel"
          style={{ top: panelPos.top, right: panelPos.right }}
        >
          <div className="user-profile-menu__summary" aria-label="Signed-in user">
            <span className="user-profile-menu__avatar user-profile-menu__avatar--summary" aria-hidden="true">
              {initials(name)}
            </span>
            <span className="user-profile-menu__identity">
              <span className="user-profile-menu__display-name">{name}</span>
              <span className="user-profile-menu__email">{email}</span>
            </span>
          </div>
          <div className="user-profile-menu__separator" />
          <WbButton
            className="user-profile-menu__logout"
            variant="tertiary"
            onClick={logout}
            role="menuitem"
          >
            <span className="user-profile-menu__logout-content">
              <WbIcon name="bds/logout/24" />
              <span>Sign Out</span>
            </span>
          </WbButton>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="user-profile-menu" ref={buttonRef}>
      <WbHeaderButton
        ref={triggerRef}
        label="Open profile menu"
        onClick={toggleMenu}
        aria-expanded={menuOpen}
        aria-haspopup="true"
        data-testid="user-profile-menu-button"
      >
        <span className="user-profile-menu__trigger">
          <span className="user-profile-menu__avatar" aria-hidden="true">
            {initials(name)}
          </span>
        </span>
      </WbHeaderButton>

      {menu}
    </div>
  );
}
